<template>
  <div>
    <h5 class="text-center mb-2">Top 10 Cities</h5>
    <table class="table table-striped table-warning table-sm table-bordered">
      <thead>
      <tr>
        <th>City</th>
        <th>Visits</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, idx) in topCities" :key="idx">
        <td>{{ item.city }}</td>
        <td>{{ item.visits }}</td>
      </tr>
      </tbody>
    </table>
    <div v-if="loading">Loading...</div>
    <div v-if="error" class="text-danger">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';

const topCities = ref([]);
const loading = ref(false);
const error = ref(null);

onMounted(/**
 * Fetches and processes data of visited links, retrieving details about cities visited.
 *
 * - Sends an HTTP GET request to the `/linksvisited` endpoint to fetch data on visited links.
 * - Logs the response data and city names found within the response.
 * - Counts occurrences of each visited city and determines the top 10 cities based on visit count.
 * - Updates the `topCities` variable with a sorted array of the top 10 visited cities.
 * - Provides proper error handling and sets an error message in case the API call fails.
 *
 * @async
 * @function
 * @throws Will set `error.value` in case the API call or data processing fails.
 */
async () => {
  loading.value = true;
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');
  try {
    const res = await axios.get(`${apiUrl}/linksvisited`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const city = item?.city || 'Unknown';
        counts[city] = (counts[city] || 0) + 1;
      });
      topCities.value = Object.entries(counts)
          .map(([city, visits]) => ({ city, visits }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 10);
    }
  } catch (e) {
    error.value = 'Failed to load city data';
  } finally {
    loading.value = false;
  }
});
</script>
